import { useState, useCallback, useRef, useEffect } from 'react';
import config from '../config/environment';

const useLudoGame = () => {
  const [gameState, setGameState] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [playerColor, setPlayerColor] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [gameStatus, setGameStatus] = useState({ isGameOver: false });
  const [players, setPlayers] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [playerId, setPlayerId] = useState(null);
  const [diceValue, setDiceValue] = useState(null);
  const [possibleMoves, setPossibleMoves] = useState([]);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const handleWebSocketMessage = useCallback((message) => {
    switch (message.type) {
      case 'joined':
        setPlayerId(message.playerId);
        setPlayerColor(message.playerInfo.color);
        setGameState(message.gameState);
        setPlayers(message.gameState.players || {});
        break;

      case 'playerJoined':
        setPlayers(prev => ({
          ...prev,
          [message.player.id]: message.player
        }));
        // Update gameState with new player
        setGameState(prev => ({
          ...prev,
          players: {
            ...(prev?.players || {}),
            [message.player.id]: message.player
          }
        }));
        break;

      case 'gameStarted':
        setGameStatus({ isGameStarted: true, isGameOver: false });
        setCurrentPlayer(message.currentPlayer);
        break;

      case 'diceRolled':
        setDiceValue(message.diceValue);
        setPossibleMoves(message.possibleMoves || []);
        // Use currentPlayer from message if available
        if (message.currentPlayer) {
          setCurrentPlayer(message.currentPlayer);
        }
        break;

      case 'moveMade':
        setGameState(message.gameState);
        setPlayers(message.gameState.players || {});
        setCurrentPlayer(message.currentPlayer);
        setDiceValue(null);
        setPossibleMoves([]);
        break;

      case 'turnChanged':
        setCurrentPlayer(message.currentPlayer);
        setDiceValue(null);
        setPossibleMoves([]);
        break;

      case 'gameEnded':
        setGameStatus({
          isGameOver: true,
          winner: message.winner,
          reason: message.reason
        });
        break;

      case 'chatMessage':
        setChatMessages(prev => [...prev, {
          id: message.id,
          playerId: message.playerId,
          playerName: message.playerName,
          text: message.text,
          timestamp: message.timestamp
        }]);
        break;

      case 'playerDisconnected':
        // Handle player disconnection
        break;

      case 'error':
        setError(message.message);
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  }, []);

  const connectToRoom = useCallback((roomId, playerName) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    setConnectionStatus('connecting');
    setError(null);
    
    try {
      const wsUrl = `${config.getWebSocketUrl()}/ws/room/${roomId}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        
        // Join the game
        wsRef.current.send(JSON.stringify({
          type: 'join',
          playerName
        }));
      };

      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error occurred');
        setConnectionStatus('error');
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectToRoom(roomId, playerName);
          }, 1000 * reconnectAttempts.current);
        } else {
          setError('Connection lost. Please refresh the page.');
        }
      };
    } catch (err) {
      setError('Failed to connect to game server');
      setConnectionStatus('error');
    }
  }, [handleWebSocketMessage]);

  const rollDice = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'rollDice'
      }));
    }
  }, []);

  const movePiece = useCallback((pieceId, steps) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'movePiece',
        pieceId,
        steps
      }));
    }
  }, []);

  const sendChatMessage = useCallback((text) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'chat',
        text
      }));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    gameState,
    isConnected,
    connectionStatus,
    error,
    playerColor,
    currentPlayer,
    gameStatus,
    players,
    chatMessages,
    playerId,
    diceValue,
    possibleMoves,
    rollDice,
    movePiece,
    sendChatMessage,
    connectToRoom
  };
};

export default useLudoGame;
